import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Flex,
  Loading,
  EmptyState
} from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import PropTypes from 'prop-types';
import { getResourceTo } from 'react/utils/utils';
import { useRouteView } from 'react/queries/RouteView';
import { getCriteriaChain } from 'react/utils/criteriaChainUtils';
import { actionCreators } from 'react/api/actions/quickAssignment';

import { Container } from './RouteListStyles';
import { RouteItem } from '../RouteItem';

function RouteList({ filterState }) {
  const routeListRef = useRef(null);
  const dispatch = useDispatch();
  const [itemWidth, setItemWidth] = useState('100%');
  const { filters, defaultValues, filterBuilderFields, routeFieldsQuery } = filterState;
  const criteriaChain = useMemo(
    () =>
      getCriteriaChain(filters || defaultValues, filterBuilderFields, false),
    [filters, filterBuilderFields, defaultValues]
  );

  const isRouteFieldsReady = routeFieldsQuery.isFetched && !routeFieldsQuery.isLoading;

  const {
    data: routesData,
    isFetching,
    status,
    fetchNextPage,
    isFetchingNextPage
  } = useRouteView(criteriaChain, isRouteFieldsReady);

  const { pages } = routesData || {};

  const isLoading = useMemo(
    () => status === 'pending' && isFetching && !isFetchingNextPage,
    [status, isFetching, isFetchingNextPage]
  );
  const isLoadingMoreRoutes = status === 'loading' || isFetchingNextPage;
  const hasNoData = status === 'success' && pages && pages[0]?.length === 0;
  const updateRoutesList = () => {
    const routes = pages?.flatMap((route) => route);
    if (routes) {
      dispatch(
        actionCreators.updateRoutesList({
          routes
        })
      );
    }
  }

  const fetchNextPageRoutes = () => {
    const addScrollListener = (event) => {
      const scrollArea = event.target;
      const delta =
        scrollArea.scrollHeight -
        scrollArea.scrollTop -
        scrollArea.getBoundingClientRect().height;

      if (Math.floor(delta) <= 0) {
        fetchNextPage();
      }
    };

    const container = routeListRef.current;

    if (container) {
      container.addEventListener('scroll', addScrollListener);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', addScrollListener);
      }
    };
  };

  const adjustRouteItemWidth = () => {
    const calculateItemWidth = () => {
      if (!routeListRef.current) return;

      const containerWidth = routeListRef.current.offsetWidth;
      const ITEM_BASE_WIDTH = 540;
      const GAP = 8;
      const PADDING_CONTAINER = 8;
      const LIST_ITEM_PADDING = 24; // total horizontal padding inside each item
      const availableWidth = containerWidth - PADDING_CONTAINER;
      const itemsPerRow = Math.floor(availableWidth / ITEM_BASE_WIDTH);

      if (itemsPerRow >= 2) {
        const totalItemWidth = availableWidth / itemsPerRow;
        const calculatedWidth = totalItemWidth - GAP - LIST_ITEM_PADDING;
        setItemWidth(`${Math.floor(calculatedWidth)}px`);
      } else {
        setItemWidth('100%');
      }
    };

    const timeoutId = setTimeout(calculateItemWidth, 0);
    const resizeObserver = new ResizeObserver(calculateItemWidth);

    if (routeListRef.current) {
      resizeObserver.observe(routeListRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  };

  useEffect(updateRoutesList, [pages]);
  useEffect(fetchNextPageRoutes, [pages]);
  useEffect(adjustRouteItemWidth, [pages, isLoading]);

  return (
    <Container dataTestId="route-list-container" itemWidth={itemWidth}>
      <Flex flexDirection="column" className="list-container">
        <Flex
          className="route-list-content"
          ref={routeListRef}
        >
          {pages?.map((page) => (
            <React.Fragment key={uuid()}>
              {page.map(({ route }) => (
                <RouteItem key={route.id} route={route} />
              ))}
            </React.Fragment>
          ))}
          <Flex
            shouldShow={hasNoData}
            justifyContent="center"
            alignItems="center"
            style={{ flex: 1 }}
          >
            <EmptyState
              type="data"
              message={getResourceTo('monetuilist.nodatafound')}
            />
          </Flex>
          <Flex
            className="loading-next-page"
            shouldShow={isLoadingMoreRoutes}
            justifyContent="center"
            alignItems="center"
          >
            <Loading message={getResourceTo('loading.message')} isVisible />
          </Flex>
        </Flex>
        <Flex
          shouldShow={isLoading}
          style={{ flex: 1 }}
          justifyContent="center"
          alignItems="center"
        >
          <Loading message={getResourceTo('loading.message')} isVisible />
        </Flex>
      </Flex>
    </Container>
  );
}

RouteList.propTypes = {
  filterState: PropTypes.object.isRequired
};

export default RouteList;
